import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

import { SingleDialog, DialogHeader, DialogContent } from '../CustomDialogs';
import useDeviceType from '../../hooks/useDeviceType';
import {
  clockInApi,
  clockOutApi,
  breakEndApi,
  breakStartApi,
  driveInApi,
  driveOutApi,
} from '../../api/userAttendance';

import { Toast } from '../Toast/Toast';

import Timer from './Timer';
import { getCurrentDateAndTime } from '../Utils/helper';

const useStyles = makeStyles(() => ({
  heading: {
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '34px',
    color: '#000',
  },
  company__name: {
    fontWeight: '600',
    lineHeight: '34px',
    color: '#6921C4',
  },
  emp__name: {
    fontWeight: '500',
    color: '#6D7D8B',
  },
  date: {
    color: '#6D7D8B',
    fontWeight: '500',
  },
  headings: {
    fontWeight: '600',
    color: '#6D7D8B',
  },
  header: {
    padding: '1rem 1rem 0',
  },
  main__popup: {
    width: 'fit-content',
    maxHeight: '100%',
    maxWidth: '40vw',
  },

  break__btn: {
    width: '100%',
    background: 'rgba(255, 96, 25, .3) !important',
    color: 'var(--color-secondary)',
    borderRadius: '100px',
  },

  drive__btn: {
    width: '100%',
    borderRadius: '100px',
  },
  blue: {
    background: '#E5D9F5 !important',
    color: 'var(--color-primary)',
  },
  green: {
    background: '#1A9B2F !important',
    color: '#FFF',
  },
  red: {
    background: '#CC3827 !important',
    color: '#FFF',
  },
  disabled__btn: {
    background: '#92959E !important',
    color: '#FFF',
  },

  main__styles: {
    padding: '1rem',
    height: 'calc(100% - 52px)',
    overflow: 'auto',
    '& .dialog__content__inner': {
      height: '100%',
    },
  },
  loader: {
    marginLeft: '5px',
    color: '#FFF',
  },
  logout__btn: {
    width: '100%',
    background: '#1A9B2F !important',
    color: '#FFF',
    borderRadius: '100px',
  },
}));

const AttendancePopup = ({ toggle, setToggle }) => {
  const classes = useStyles();
  const { isMobile } = useDeviceType();
  const dispatch = useDispatch();
  const [date, setDate] = useState();
  const [loading, setLoading] = useState();
  const [showLogoutBtn, setShowLogoutBtn] = useState(false);

  useEffect(() => {
    if (toggle) {
      const currentTime = getCurrentDateAndTime();
      setDate(currentTime?.toFormat('DDDD'));
    }
  }, [toggle]);

  const { userData = '' } = useSelector((state) => state.coUser);

  let { attendance = '' } = userData;
  attendance = attendance?.[0] || '';

  // updating co-user redux store
  const updateUserStoreDate = (data) => {
    dispatch({
      type: 'UPDATE_USER_DATA',
      payload: {
        data: {
          ...userData,
          attendance: data ? [data] : [],
        },
      },
    });
  };

  // handle api error
  const hadleApiError = (error) => {
    const _error = error?.response?.data?.message || error?.message;
    if (_error === "User Can't be Logged in the same Date") {
      setShowLogoutBtn(true);
    }
    Toast('error', _error);
  };

  // user clock in clock out
  const handleClockInClockOut = async (usedIn) => {
    setLoading(usedIn);
    if (usedIn === 'checkIn') {
      // clock in
      try {
        const { data } = await clockInApi();
        updateUserStoreDate(data?.data || '');
      } catch (error) {
        hadleApiError(error || 'Error While Clockin In.');
      }
    } else {
      // clock out case

      const attendanceId = attendance?.id || '';

      try {
        const { data } = await clockOutApi({
          attendanceId,
        });
        updateUserStoreDate(data?.data || '');
      } catch (error) {
        hadleApiError(error || 'Error While Clocking Out');
      }
    }
    setLoading();
  };

  // user break start break end
  const handleStartBreakEndBreak = async (usedIn) => {
    setLoading(usedIn);

    if (usedIn === 'breakStart') {
      try {
        const { data } = await breakStartApi({ attendanceId: attendance?.id });
        updateUserStoreDate(data?.data || '');
      } catch (error) {
        hadleApiError(error || 'Error While Breaking Start');
      }
    } else {
      // break end case

      const breakId = attendance?.attendance_details?.[0]?.id || '';
      const attendanceId = attendance?.id || '';

      try {
        const { data } = await breakEndApi({
          breakId,
          attendanceId,
        });
        updateUserStoreDate(data?.data || '');
      } catch (error) {
        hadleApiError(error || 'Error While Breaking End');
      }
    }
    setLoading();
  };

  // user drive in drive out
  const handleDriveInDriveOut = async (usedIn) => {
    setLoading(usedIn);

    if (usedIn === 'driveIn') {
      try {
        const { data } = await driveInApi({ attendanceId: attendance?.id });
        updateUserStoreDate(data?.data || '');
      } catch (error) {
        hadleApiError(error || 'Error While Driving In');
      }
    } else {
      // drive out case

      const driveId = attendance?.attendance_details?.[0]?.id || '';
      const attendanceId = attendance?.id || '';

      try {
        const { data } = await driveOutApi({
          driveId,
          attendanceId,
        });
        updateUserStoreDate(data?.data || '');
      } catch (error) {
        hadleApiError(error || 'Error While Driving Out');
      }
    }
    setLoading();
  };

  const handleDisabledStates = (usedId) => {
    const { status = '' } = attendance || '';
    const invalidStatusForClockIn = ['checkIn', 'driveIn', 'breakStart'];
    const invalidStatusForClockOut = ['checkOut', 'driveIn', 'breakStart'];
    const invalidStatusForBreakStart = ['driveIn', 'checkOut'];
    const invalidStatusForBreakEnd = ['driveIn', 'checkOut'];
    const invalidStatusForDriveIn = ['checkOut', 'driveIn', 'breakStart'];

    switch (usedId) {
      case 'checkIn':
        return (
          invalidStatusForClockIn.includes(status) || loading === 'checkIn'
        );
      case 'checkOut':
        return (
          !status ||
          invalidStatusForClockOut.includes(status) ||
          loading === 'checkOut'
        );
      case 'breakStart':
        return (
          !status ||
          invalidStatusForBreakStart.includes(status) ||
          loading === 'breakStart'
        );
      case 'breakEnd':
        return (
          !status ||
          invalidStatusForBreakEnd.includes(status) ||
          loading === 'breakEnd'
        );
      case 'driveIn':
        return (
          !status ||
          invalidStatusForDriveIn.includes(status) ||
          loading === 'driveIn'
        );
      case 'driveOut':
        return (
          (usedId === 'driveOut' && status !== 'driveIn') ||
          loading === 'driveOut'
        );
      default:
        return false;
    }
  };
  const _userData = JSON.parse(localStorage.getItem('userData')) || '';

  const { userStore = '' } = _userData;
  const { businessName = '' } = userStore;

  const showCrossBtn = !userData?.forceClockIn
    ? true
    : userData?.forceClockIn &&
      userData?.showShiftTimings &&
      (!userData?.attendance ||
        userData?.attendance?.length === 0 ||
        userData?.attendance?.[0]?.status === 'checkOut')
    ? false
    : true;
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/signin';
  };
  return (
    <SingleDialog styles={classes.main__popup} toggle={toggle}>
      <DialogHeader headerStyle={classes.header}>
        <Typography className={classes.heading}>Clock In/Clock Out</Typography>
        {showCrossBtn && (
          <IconButton className='c-pointer' onClick={() => setToggle(false)}>
            <CloseRoundedIcon />
          </IconButton>
        )}
      </DialogHeader>
      <DialogContent mainStyles={classes.main__styles}>
        <Grid container spacing={1}>
          <Grid item md={isMobile ? 12 : 6}>
            <Grid container spacing={1}>
              <Grid item md={12}>
                <Typography variant='h2' className={classes.company__name}>
                  {businessName}
                </Typography>
                <Typography variant='h5' className={classes.date}>
                  {/* Monday, 1st Jan 2024 */}
                  {date}
                </Typography>
              </Grid>
              <Grid container item>
                <Grid item md={6}>
                  <Typography variant='h6' className={classes.headings}>
                    Work Time
                  </Typography>
                </Grid>
                <Grid item md={6}>
                  <Typography
                    variant='h6'
                    className={classes.headings}
                    style={{ color: '#1A9B2F' }}
                  >
                    {attendance?.status === 'checkIn' ? (
                      <Timer attendance={attendance} />
                    ) : (
                      attendance?.workedHours || '00:00:00'
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item>
                <Grid item md={6}>
                  <Typography variant='h6' className={classes.headings}>
                    Break Time
                  </Typography>
                </Grid>
                <Grid item md={6}>
                  <Typography
                    variant='h6'
                    className={classes.headings}
                    style={{ color: '#6921C4' }}
                  >
                    {attendance?.status === 'breakStart' ? (
                      <Timer attendance={attendance} />
                    ) : (
                      attendance?.totalBreakHours || '00:00:00'
                    )}
                  </Typography>
                </Grid>
              </Grid>
              {userData?.driveInDriveOut && (
                <Grid container item>
                  <Grid item md={6}>
                    <Typography variant='h6' className={classes.headings}>
                      Drive Time
                    </Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography
                      variant='h6'
                      className={classes.headings}
                      style={{ color: '#FF6019' }}
                    >
                      {attendance?.status === 'driveIn' ? (
                        <Timer attendance={attendance} />
                      ) : (
                        attendance?.totalDriveHours || '00:00:00'
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              <Grid item md={12}>
                <Typography variant='h5' className={classes.emp__name}>
                  Employee Name
                </Typography>
                <Typography variant='h6'>{userData?.name || '-'}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={isMobile ? 12 : 6}>
            <Grid container spacing={1}>
              <Grid container item spacing={1}>
                <Grid item md={6}>
                  <Button
                    className={clsx(
                      classes.green,
                      classes.drive__btn,

                      handleDisabledStates('checkIn') && classes.disabled__btn,
                    )}
                    disabled={handleDisabledStates('checkIn')}
                    onClick={() => handleClockInClockOut('checkIn')}
                  >
                    Clock In
                    {loading === 'checkIn' && (
                      <CircularProgress className={classes.loader} size={15} />
                    )}
                  </Button>
                </Grid>
                <Grid item md={6}>
                  <Button
                    className={clsx(
                      classes.red,
                      classes.drive__btn,
                      handleDisabledStates('checkOut') && classes.disabled__btn,
                    )}
                    disabled={handleDisabledStates('checkOut')}
                    onClick={() => handleClockInClockOut('checkOut')}
                  >
                    Clock Out
                    {loading === 'checkOut' && (
                      <CircularProgress className={classes.loader} size={15} />
                    )}
                  </Button>
                </Grid>
              </Grid>
              {userData?.driveInDriveOut && (
                <Grid container item spacing={1}>
                  <Grid item md={6}>
                    <Button
                      className={clsx(
                        classes.blue,
                        classes.drive__btn,
                        handleDisabledStates('driveIn') &&
                          classes.disabled__btn,
                      )}
                      disabled={handleDisabledStates('driveIn')}
                      onClick={() => handleDriveInDriveOut('driveIn')}
                    >
                      Drive In
                      {loading === 'driveIn' && (
                        <CircularProgress
                          className={classes.loader}
                          size={15}
                        />
                      )}
                    </Button>
                  </Grid>
                  <Grid item md={6}>
                    <Button
                      className={clsx(
                        classes.blue,
                        classes.drive__btn,
                        handleDisabledStates('driveOut') &&
                          classes.disabled__btn,
                      )}
                      disabled={handleDisabledStates('driveOut')}
                      onClick={() => handleDriveInDriveOut('driveOut')}
                    >
                      Drive Out
                      {loading === 'driveOut' && (
                        <CircularProgress
                          className={classes.loader}
                          size={15}
                        />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              )}
              <Grid item md={12}>
                <Button
                  className={clsx(
                    classes.break__btn,
                    handleDisabledStates(
                      attendance?.status === 'breakStart'
                        ? 'breakEnd'
                        : 'breakStart',
                    ) && classes.disabled__btn,
                  )}
                  disabled={handleDisabledStates(
                    attendance?.status === 'breakStart'
                      ? 'breakEnd'
                      : 'breakStart',
                  )}
                  onClick={
                    attendance?.status === 'breakStart'
                      ? () => handleStartBreakEndBreak('breakEnd')
                      : () => handleStartBreakEndBreak('breakStart')
                  }
                >
                  {attendance?.status === 'breakStart'
                    ? 'End Break'
                    : 'Start Break'}
                  {(loading === 'breakStart' || loading === 'breakEnd') && (
                    <CircularProgress className={classes.loader} size={15} />
                  )}
                </Button>
              </Grid>
              {showLogoutBtn && (
                <Grid item md={12}>
                  <Button
                    onClick={handleLogout}
                    className={classes.logout__btn}
                  >
                    Logout
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </SingleDialog>
  );
};

export default AttendancePopup;
